// frontend-dash/src/context/AuthContext.js
import React, { createContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

// API URL Konfiguration
const getApiUrl = () => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  if (backendUrl) {
    if (process.env.NODE_ENV === 'production' && backendUrl.startsWith('http://')) {
      console.warn('⚠️  SICHERHEITSWARNUNG: Backend-URL verwendet HTTP statt HTTPS in Produktion!');
    }
    const cleanUrl = backendUrl.endsWith('/') ? backendUrl.slice(0, -1) : backendUrl;
    return cleanUrl.endsWith('/api') ? cleanUrl : `${cleanUrl}/api`;
  }

  if (process.env.NODE_ENV !== 'production') {
    return 'http://localhost:5000/api';
  }

  return 'https://pfadfinder-public-api.suppenchris.workers.dev/api';
};

const API_URL = getApiUrl();
const rawPublicUrl = process.env.REACT_APP_PUBLIC_API_URL || API_URL;
let PUBLIC_API_URL = rawPublicUrl.endsWith('/') ? rawPublicUrl.slice(0, -1) : rawPublicUrl;

if (!PUBLIC_API_URL.endsWith('/api')) {
  PUBLIC_API_URL = `${PUBLIC_API_URL}/api`;
}


// Cookie Domain für Cross-Subdomain Auth
const COOKIE_DOMAIN = process.env.REACT_APP_COOKIE_DOMAIN || '.pfadfinder-wutoeschingen.org';
const getMainUrl = () => {
  if (process.env.REACT_APP_MAIN_URL) return process.env.REACT_APP_MAIN_URL;
  if (typeof window !== 'undefined' && window.location.hostname) {
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      return `http://${window.location.hostname}:3000`;
    }
    return `${window.location.protocol}//${window.location.hostname.replace(/^dash\./, '')}`;
  }
  return 'https://pfadfinder-wutoeschingen.org';
};

const MAIN_URL = getMainUrl();

// Cookie Helpers
const setCookie = (name, value, days = 30) => {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  // In development, don't set domain (localhost doesn't support domain cookies)
  const domainPart = process.env.NODE_ENV === 'production' 
    ? `; domain=${COOKIE_DOMAIN}` 
    : '';
  const securePart = process.env.NODE_ENV === 'production' 
    ? '; Secure; SameSite=Lax' 
    : '; SameSite=Lax';
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/${domainPart}${securePart}`;
};

const getCookie = (name) => {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
};

const deleteCookie = (name) => {
  const domainPart = process.env.NODE_ENV === 'production' 
    ? `; domain=${COOKIE_DOMAIN}` 
    : '';
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domainPart}`;
};

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Try cookie first (cross-domain), then fallback to localStorage
    const cookieData = getCookie('pfadi_auth');
    const storedUser = cookieData ? JSON.parse(cookieData) : null;
    const localUser = localStorage.getItem('user');
    
    const userData = storedUser || (localUser ? JSON.parse(localUser) : null);
    
    if (userData) {
      if (userData.token) {
        try {
          const decoded = jwtDecode(userData.token);
          if (decoded.exp * 1000 < Date.now()) {
            // Token is expired
            deleteCookie('pfadi_auth');
            localStorage.removeItem('user');
            localStorage.removeItem('userInfo');
            setUser(null);
          } else {
            setUser(userData);
            // Sync to both storage mechanisms
            setCookie('pfadi_auth', JSON.stringify(userData));
            localStorage.setItem('user', JSON.stringify(userData));
          }
        } catch (e) {
          deleteCookie('pfadi_auth');
          localStorage.removeItem('user');
          localStorage.removeItem('userInfo');
          setUser(null);
        }
      } else {
        setUser(userData);
      }
    }
    setLoading(false);
  }, []);

  const login = useCallback(async (email, password, recaptchaToken) => {
    try {
      const config = {
        headers: {
          'Content-Type': 'application/json',
        },
      };
      const res = await axios.post(`${PUBLIC_API_URL}/users/login`, { email, password, recaptchaToken }, config);
      setUser(res.data);
      // Store in both cookie and localStorage
      setCookie('pfadi_auth', JSON.stringify(res.data));
      localStorage.setItem('user', JSON.stringify(res.data));
      return true;
    } catch (error) {
      console.error('Login failed:', error.response ? error.response.data.message : error.message);
      setUser(null);
      if (error.response && error.response.data && error.response.data.message) {
        throw new Error(error.response.data.message);
      } else if (error.message) {
        throw new Error(error.message);
      } else {
        throw new Error('Unbekannter Login-Fehler.');
      }
    }
  }, []);

  const googleLogin = useCallback((token) => {
    try {
      const decoded = jwtDecode(token);
      const userData = { token, ...decoded };
      setUser(userData);
      setCookie('pfadi_auth', JSON.stringify(userData));
      localStorage.setItem('user', JSON.stringify(userData));
    } catch (error) {
      console.error('Google login failed:', error);
      throw error;
    }
  }, []);

  const registerUser = useCallback(async (email, password, registrationCode, userData, avatarUrl) => {
    try {
      const config = {
        headers: {
          'Content-Type': 'application/json',
        },
      };
      const { data } = await axios.post(`${PUBLIC_API_URL}/auth/register`, {
        email,
        password,
        registrationCode,
        userData,
        avatarUrl
      }, config);

      if (data) {
        await login(email, password);
      }
    } catch (error) {
      if (!error.response || error.response.status !== 409) {
        console.error('Registration failed:', error);
      }
      throw error;
    }
  }, [login]);

  const logout = useCallback(() => {
    setUser(null);
    deleteCookie('pfadi_auth');
    localStorage.removeItem('user');
    localStorage.removeItem('userInfo');
  }, []);

  /**
   * Helper to get absolute asset URL for R2 assets.
   */
  const getAssetUrl = useCallback((path, options = {}) => {
    if (!path) return '';
    
    // Resolve path if an object (e.g. { url: '...', src: '...' }) or non-string was passed
    let resolvedPath = path;
    if (typeof resolvedPath === 'object' && resolvedPath !== null) {
      resolvedPath = resolvedPath.url || resolvedPath.src || resolvedPath.path || resolvedPath.key || resolvedPath.modelUrl || '';
    }
    
    if (typeof resolvedPath !== 'string' || !resolvedPath.trim()) {
      return '';
    }

    let absoluteUrl = resolvedPath.trim();
    
    if (!absoluteUrl.startsWith('http') && !absoluteUrl.startsWith('data:') && !absoluteUrl.startsWith('blob:')) {
      if (absoluteUrl.startsWith('/api/assets/')) {
        absoluteUrl = `${PUBLIC_API_URL}${absoluteUrl.replace(/^\/api/, '')}`;
      } else if (absoluteUrl.startsWith('/uploads/')) {
        absoluteUrl = `${API_URL}${absoluteUrl}`;
      } else if (absoluteUrl.startsWith('/')) {
        absoluteUrl = absoluteUrl;
      } else {
        absoluteUrl = `${PUBLIC_API_URL}/assets/${absoluteUrl}`;
      }
    }

    // In browser production, ensure absolute origin so image proxy can fetch relative asset paths
    if (typeof absoluteUrl === 'string' && absoluteUrl.startsWith('/') && typeof window !== 'undefined' && window.location && window.location.origin) {
      if (!window.location.hostname.includes('localhost') && !window.location.hostname.includes('127.0.0.1')) {
        absoluteUrl = `${window.location.origin}${absoluteUrl}`;
      }
    }

    if (options && (options.width || options.quality)) {
      if (typeof absoluteUrl === 'string' && (absoluteUrl.includes('localhost') || absoluteUrl.includes('127.0.0.1') || absoluteUrl.startsWith('/'))) {
        return absoluteUrl;
      }
      const proxyUrl = new URL('https://wsrv.nl/');
      proxyUrl.searchParams.append('url', absoluteUrl);
      if (options.width) proxyUrl.searchParams.append('w', options.width);
      if (options.quality) proxyUrl.searchParams.append('q', options.quality);
      proxyUrl.searchParams.append('output', 'webp');
      
      return proxyUrl.toString();
    }

    return absoluteUrl;
  }, [API_URL, PUBLIC_API_URL]);

  if (loading) {
    return <div>Lädt...</div>;
  }

  return (
    <AuthContext.Provider value={{ user, login, registerUser, logout, googleLogin, API_URL, PUBLIC_API_URL, MAIN_URL, getAssetUrl }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
